// Policy Comparator engine — the AVRASTRO differentiator (legacy v230 Group Plans & Comparator,
// v232 Protection & Cost Clarity, v233 Flexible Plans, Savings & Combination), rebuilt as pure,
// tested math. Keeps the legacy vocabulary strictly separated:
//   Sum Insured / Benefit  ≠  Monthly Premium  ≠  Total Premium Cost  ≠  Savings Contribution
//   ≠  Projected Value  ≠  Guaranteed Value.

export interface PlanInput {
  label: 'A' | 'B' | 'C';
  insurer: string;
  product: string;
  kind: 'protection' | 'savings' | 'combined';
  /** monthly premium in year 1 */
  monthlyPremium: number;
  /** yearly premium indexation in % (0 = level premium) */
  indexationPct: number;
  termYears: number;
  /** death / main benefit */
  sumInsured: number;
  /** additional riders (critical illness, waiver, hospital...) — informational, priced inside the premium */
  riders: string[];
  /** % of each premium that goes to savings (0 for pure protection, 100 for pure savings) */
  savingsSharePct: number;
  /** months at the start during which nothing is allocated (legacy "6/9/24 months without allocation") */
  allocationDelayMonths: number;
  /** extra charge in % of the savings contribution for year 1 and year 2 (legacy "50% first year, 75% second") */
  year1ChargePct: number;
  year2ChargePct: number;
  /** yearly management fee on the fund in % */
  annualFeePct: number;
  /** assumed gross yearly return in % */
  assumedReturnPct: number;
  /** contractually guaranteed value at maturity (optional) */
  guaranteedMaturity?: number | null;
  quotationNumber?: string;
}

export interface YearRow {
  year: number;
  premium: number;          // premiums paid in that year
  paidToDate: number;       // total premium cost so far
  savingsContribution: number; // allocated to the fund this year (after delay and charges)
  fundValue: number;        // projected value at year end
  cover: number;            // sum insured in force
}

export interface PlanResult {
  label: PlanInput['label'];
  years: YearRow[];
  totalPaid: number;
  totalSavingsContribution: number;
  projectedValue: number;
  guaranteedValue: number | null;
  /** premiums that bought protection / charges, i.e. not saved */
  costOfProtectionAndCharges: number;
  /** what the plan costs net of the projected value (negative = gain) */
  netCost: number;
  /** yearly premium per 1 000 of cover (protection efficiency) */
  premiumPer1000Cover: number | null;
  /** first year in which projected value ≥ premiums paid (null if never) */
  breakEvenYear: number | null;
  /** chargesImpact: value lost to delay + charges + fees vs. a charge-free fund */
  chargesImpact: number;
  warnings: ('no_cover' | 'no_term' | 'long_delay' | 'high_charges' | 'fees_exceed_return')[];
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, Number.isFinite(n) ? n : 0));

export function projectPlan(p: PlanInput): PlanResult {
  const years: YearRow[] = [];
  const term = Math.round(clamp(p.termYears, 0, 60));
  const share = clamp(p.savingsSharePct, 0, 100) / 100;
  const netGrowth = clamp(p.assumedReturnPct, -50, 50) / 100 - clamp(p.annualFeePct, 0, 20) / 100;
  let fund = 0, ideal = 0, paid = 0, saved = 0, month = 0;
  let breakEven: number | null = null;
  for (let y = 1; y <= term; y++) {
    const monthly = Math.max(0, p.monthlyPremium) * Math.pow(1 + clamp(p.indexationPct, -20, 50) / 100, y - 1);
    const charge = y === 1 ? clamp(p.year1ChargePct, 0, 100) / 100 : y === 2 ? clamp(p.year2ChargePct, 0, 100) / 100 : 0;
    let yearAlloc = 0;
    for (let m = 0; m < 12; m++) {
      month++;
      paid += monthly;
      const contrib = monthly * share;
      const alloc = month <= p.allocationDelayMonths ? 0 : contrib * (1 - charge);
      yearAlloc += alloc;
      // monthly compounding of the net return
      fund = (fund + alloc) * Math.pow(1 + netGrowth, 1 / 12);
      ideal = (ideal + contrib) * Math.pow(1 + clamp(p.assumedReturnPct, -50, 50) / 100, 1 / 12);
    }
    saved += yearAlloc;
    years.push({ year: y, premium: r2(monthly * 12), paidToDate: r2(paid), savingsContribution: r2(yearAlloc), fundValue: r2(fund), cover: p.sumInsured });
    if (breakEven === null && share > 0 && fund >= paid) breakEven = y;
  }
  const annual1 = Math.max(0, p.monthlyPremium) * 12;
  const warnings: PlanResult['warnings'] = [];
  if (p.kind !== 'savings' && !(p.sumInsured > 0)) warnings.push('no_cover');
  if (term === 0) warnings.push('no_term');
  if (p.allocationDelayMonths >= 12) warnings.push('long_delay');
  if (p.year1ChargePct + p.year2ChargePct >= 100) warnings.push('high_charges');
  if (share > 0 && p.annualFeePct >= p.assumedReturnPct) warnings.push('fees_exceed_return');
  return {
    label: p.label, years, totalPaid: r2(paid), totalSavingsContribution: r2(saved), projectedValue: r2(fund),
    guaranteedValue: p.guaranteedMaturity ?? null, costOfProtectionAndCharges: r2(paid - saved), netCost: r2(paid - fund),
    premiumPer1000Cover: p.sumInsured > 0 ? r2((annual1 / p.sumInsured) * 1000) : null,
    breakEvenYear: breakEven, chargesImpact: r2(Math.max(0, ideal - fund)), warnings,
  };
}

export interface ComparisonVerdict {
  results: PlanResult[];
  /** per metric, which plan is best (lowest cost / highest value) */
  best: { lowestPremium?: string; highestCover?: string; highestProjected?: string; lowestNetCost?: string; bestCoverEfficiency?: string; lowestCharges?: string };
}

export function comparePlans(plans: PlanInput[]): ComparisonVerdict {
  const results = plans.map(projectPlan);
  const pick = <T,>(arr: T[], f: (x: T) => number | null, dir: 'min' | 'max') => {
    let best: T | undefined, bv = 0;
    for (const x of arr) { const v = f(x); if (v === null || !Number.isFinite(v)) continue; if (best === undefined || (dir === 'min' ? v < bv : v > bv)) { best = x; bv = v; } }
    return best;
  };
  const byLabel = (l?: { label: string }) => l?.label;
  return {
    results,
    best: {
      lowestPremium: byLabel(pick(plans, (p) => p.monthlyPremium, 'min')),
      highestCover: byLabel(pick(plans.filter((p) => p.sumInsured > 0), (p) => p.sumInsured, 'max')),
      highestProjected: byLabel(pick(results.filter((r) => r.projectedValue > 0), (r) => r.projectedValue, 'max')),
      lowestNetCost: byLabel(pick(results, (r) => r.netCost, 'min')),
      bestCoverEfficiency: byLabel(pick(results, (r) => r.premiumPer1000Cover, 'min')),
      lowestCharges: byLabel(pick(results.filter((r) => r.totalSavingsContribution > 0), (r) => r.chargesImpact, 'min')),
    },
  };
}

export function emptyPlan(label: PlanInput['label']): PlanInput {
  return {
    label, insurer: '', product: '', kind: 'combined', monthlyPremium: 0, indexationPct: 0, termYears: 20, sumInsured: 0, riders: [],
    savingsSharePct: 60, allocationDelayMonths: 0, year1ChargePct: 0, year2ChargePct: 0, annualFeePct: 1, assumedReturnPct: 4, guaranteedMaturity: null,
  };
}

/** Parse the stored `plans` JSON defensively (drafts, imported data, older app versions). */
export function normalizePlans(raw: unknown): PlanInput[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.slice(0, 3).map((x, i) => ({ ...emptyPlan((['A', 'B', 'C'] as const)[i]!), ...(x && typeof x === 'object' ? x : {}), label: (['A', 'B', 'C'] as const)[i]! }));
}
